import { useEffect, useMemo, useState } from "react";
import { getCobrancasHistory } from "../services/payments";
import { useUserState } from "../state/user";
import { useSelectedPaymentConfiguration } from "../state/selectedPaymentConfiguration";
import { filterTransactionsByConfiguration } from "../utils/transactions";
import axios from "axios";

interface ChargeLineItem {
  code: string;
  description: string;
  quantity: number;
  value: number;
}

export interface ChargeItem {
  id: string;
  created_at: string;
  paid_at: string | null;
  customer: { name: string };
  status: string;
  value: number;
  urlReceipt?: string;
  items: ChargeLineItem[];
}

interface ChargesHistoryResponse {
  items: ChargeItem[];
  total: number;
}

interface UseChargesHistoryParams {
  page: number;
  rowsPerPage: number;
  filters: {
    dates: [Date | null, Date | null];
    status: string;
    prices: [number | null, number | null];
    searchTxt?: string;
  };
  sort: {
    field: string;
    by: string;
  };
}

export default function useChargesHistory({
  page,
  rowsPerPage,
  filters,
  sort
}: UseChargesHistoryParams) {
  const [data, setData] = useState<ChargesHistoryResponse | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  const { token, user, loading: loadingUser } = useUserState();
  // The API cannot filter by billing entity, so the selected company is applied
  // to the page that came back. Same trade-off as the text search below: the
  // pagination stays server side, so a page can show fewer rows than requested.
  const { configId } = useSelectedPaymentConfiguration();

  useEffect(() => {
    (async function() {
      try {
        if (loadingUser) return;
        setLoading(true);
        const result = await getCobrancasHistory({
          authToken: token,
          ...(filters.dates[0] && { dataIn: filters.dates[0].toISOString() }),
          ...(filters.dates[1] && { dataEnd: filters.dates[1].toISOString() }),
          ...(filters.status && { status: filters.status }),
          ...(filters.prices[0] && { minPrice: filters.prices[0] }),
          ...(filters.prices[1] && { maxPrice: filters.prices[1] }),
          ...(sort.field && { sortField: sort.field }),
          ...(sort.by && { sortBy: sort.by }),
          accountId: user.accountStatus.paymentId,
          page,
          rowsPerPage
        });
        setData(result as ChargesHistoryResponse);
      } catch (err) {
        if (!axios.isCancel(err)) {
          setError(err as Error);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [
    filters.dates,
    filters.prices,
    filters.status,
    loadingUser,
    page,
    rowsPerPage,
    sort.by,
    sort.field,
    token,
    user.accountStatus.paymentId
  ]);

  const filteredItems = useMemo(() => {
    const allItems =
      filterTransactionsByConfiguration<ChargeItem>(data?.items, configId) ??
      [];
    const searchTxt = filters.searchTxt?.toLowerCase() ?? "";
    if (!searchTxt) return allItems;
    return allItems.filter(row =>
      row.customer?.name?.toLowerCase().includes(searchTxt)
    );
  }, [data?.items, filters.searchTxt, configId]);

  return { filteredItems, total: data?.total ?? 0, error, loading };
}
